import type { BECategory } from 'constants/categories';
import type { SportsMarketType } from 'lib/bff/types/feed.types';
import type { NumericTarget } from 'utils/numericTarget';

import type { MarketVo } from './index';
import type { SettlementSource } from './market';
import type { FeeType, ImportantInfo } from './series';

export type CollateraReturnType = 'MECNET' | 'MECNETNO' | 'DIRECNET' | '';

/** Whether the event's markets are mutually exclusive; the exchange derives `mutually_exclusive` the same way. */
export const isMecnetEvent = (event: { collateral_return_type?: CollateraReturnType } | null | undefined): boolean =>
    event?.collateral_return_type === 'MECNET';

export interface Bucket {
    midpoint: number;
    probability: number;
    adjustedTarget: NumericTarget;
}

export type CommodityCadence = 'yearly' | 'quarterly' | 'monthly' | 'weekly' | 'daily';

// Presence of this field signals that live data is available for the event via
// `live_data/events/<event_ticker>`. Sports events mostly fetch live data via
// `live_data/milestones/<milestone_id>` instead, but that path doesn't cover every
// case — not all crypto events have milestones, hourly weather and Rotten Tomatoes
// have no milestones yet, etc. — so these event types fetch by event ticker rather
// than milestone.
export enum LiveDataEventType {
    WEATHER_HOURLY = 'weather_hourly',
    COMMODITIES = 'commodities',
    ROTTEN_TOMATOES = 'rotten_tomatoes',
    NETFLIX = 'netflix',
    ARTIST_STREAMS = 'artist_streams',
    ART = 'art',
    SOCIAL_MEDIA = 'social_media',
    COLLECTIBLES = 'collectibles',
    // Provider-neutral scalar {t, v} series served by
    // /v1/live_data/events/{ticker} with type "timeseries" and rendered by
    // EventTimeseriesGraphWrapper. live_data_provider names the vendor
    // (carbonarc, bea, middesk, ...); the payload is fully backend-shaped.
    TIMESERIES = 'timeseries',
}

export interface ImportantInfoVo {
    id: string;
    title: string;
    message: string;
    markdown: string;
}

export interface EventProductMetadataVo {
    important_info?: ImportantInfoVo;
    competition_scope?: string;
    competition?: string;
    custom_image_url?: string;
    props_title?: string;
    promoted_milestone_id?: string;
    live_data_event_type?: LiveDataEventType;
    // Vendor behind a shared live_data_event_type (the "timeseries" family:
    // "carbonarc", "bea", ...). Set alongside live_data_event_type by market
    // ops; the pair is the routing stamp.
    live_data_provider?: string;
    sports_market_type?: SportsMarketType;
    preferred_structured_target_type?: string;
    [key: string]: string | ImportantInfoVo | undefined;
}

/** Plain-data mirror of `stores/domain/Event`'s parsed product metadata — kept distinct
 * from `EventProductMetadataVo` above (the raw REST shape) since this also carries the
 * `cadence` field the domain layer derives, not present on the wire type. */
/**
 * Image-bearing subset of the `product_metadata` blob carried by BFF cards and search-series results.
 * Those blobs merge event and series metadata, so `image_url` is the series' versioned artwork URL
 * (`SeriesVo.image_url`) when the series has one. Events themselves never carry `image_url`.
 */
export type ImageProductMetadata = {
    custom_image_url?: string;
    image_url?: string;
};

export type EventProductMetadata = {
    important_info?: ImportantInfo;
    competition_scope?: string;
    competition?: string;
    custom_image_url?: string;
    props_title?: string;
    promoted_milestone_id?: string;
    live_data_event_type?: LiveDataEventType;
    live_data_provider?: string;
    metric_key?: string;
    cadence?: CommodityCadence;
    [key: string]: string | ImportantInfo | undefined;
};

/**
 * A sports game event's `target_datetime` is its expected resolution, stamped this long after the
 * milestone (game) start — it is not the game time. Subtracting it estimates the start when the
 * milestone isn't at hand.
 */
export const SPORTS_GAME_TARGET_AFTER_START_MS = 6 * 60 * 60 * 1000;

export interface EventVo {
    ticker: string;
    series_ticker: string;
    exchange_shard?: number;
    target_datetime: string;
    max_close_ts?: string;
    mutually_exclusive_side?: string;
    collateral_return_type?: CollateraReturnType;
    title: string;
    category: BECategory;
    settle_details: string;
    settlement_sources: SettlementSource[];
    underlying: string;
    sub_title: string;
    markets: MarketVo[];
    product_metadata?: EventProductMetadataVo;
    product_metadata_derived?: { [variable: string]: string };
    /** The event page header title: `product_metadata_derived.trading_page_title` when present, else `title` (exchange-infra `ui.EventHeaderTitle`). */
    header_title?: string;
    status?: string;
    close_date?: string;
    employment_collection_required?: boolean;
    fee_type_override?: FeeType;
    fee_multiplier_override?: number;
}
